import type {
	CreateInsuranceFormData,
	InsuranceWithGroup,
} from "@/types/insurance/insurance";

/**
 * Configuration drift between a customer's saved insurance row and the
 * canonical product-definition template for the same `type`. Drift here
 * is purely *structural* — value differences are treated as intentional
 * admin customisations and are not flagged.
 */
export interface DriftedProduct {
	/** Customer's saved insurance row id (the value stored in `insurances.id`). */
	id: string;
	type: string;
	/** Parameter types in the catalog template that the customer's row is missing. */
	missingParams: string[];
	/** Parameter types on the customer's row that no longer exist in the catalog. */
	extraParams: string[];
}

/**
 * A saved insurance row whose `insurance_type` no longer exists in the
 * product-definition catalog at all (the entire product has been retired).
 */
export interface OrphanProduct {
	id: string;
	type: string;
}

export interface DriftReport {
	drifted: DriftedProduct[];
	orphan: OrphanProduct[];
}

/**
 * Walks `customerInsurances` against `productDefinitions`, returning a
 * structural diff per product. Pure and side-effect-free; safe to call from
 * effects or tests.
 */
export const computeProductDrift = (
	customerInsurances: ReadonlyArray<InsuranceWithGroup>,
	productDefinitions: ReadonlyArray<CreateInsuranceFormData>,
): DriftReport => {
	const templateByType = new Map(
		productDefinitions.map((def) => [def.type, def] as const),
	);

	const drifted: DriftedProduct[] = [];
	const orphan: OrphanProduct[] = [];

	for (const ins of customerInsurances) {
		const template = templateByType.get(ins.type);
		if (!template) {
			orphan.push({ id: ins.id, type: ins.type });
			continue;
		}

		const templateTypes = new Set(
			(template.parameters ?? []).map((p) => p.type),
		);
		const rowTypes = new Set((ins.params ?? []).map((p) => p.type));

		const missingParams: string[] = [];
		for (const t of templateTypes) {
			if (!rowTypes.has(t)) missingParams.push(t);
		}

		const extraParams: string[] = [];
		for (const t of rowTypes) {
			if (!templateTypes.has(t)) extraParams.push(t);
		}

		if (missingParams.length > 0 || extraParams.length > 0) {
			drifted.push({ id: ins.id, type: ins.type, missingParams, extraParams });
		}
	}

	return { drifted, orphan };
};

/**
 * Emits a `console.groupCollapsed` summary of the drift report. Stays silent
 * when nothing has drifted. Designed to live alongside the existing `[i18n]`
 * / `[debug]` console messages used in this codebase.
 *
 * Pass a `translate` resolver (e.g. a closure over the active i18next `t`
 * function) to surface a human-readable name for each drifted/orphan
 * product. The module itself stays free of i18n coupling.
 */
export const logProductDrift = (
	report: DriftReport,
	customerName: string,
	translate?: (type: string) => string,
): void => {
	if (report.drifted.length === 0 && report.orphan.length === 0) {
		return;
	}

	const labelFor = (type: string): string => {
		if (!translate) return type;
		const translated = translate(type);
		// `t()` falls back to the key when no translation is found; in that
		// case prefer the raw type so the table doesn't show a noisy key.
		return translated && translated !== type ? translated : type;
	};

	console.groupCollapsed(
		`[insurance-drift] "${customerName}" — ${report.drifted.length} drifted, ${report.orphan.length} orphan`,
	);

	if (report.drifted.length > 0) {
		console.log(
			"Parameter drift (missing = added to catalog after this customer was created):",
		);
		console.table(
			report.drifted.map((d) => ({
				id: d.id,
				type: d.type,
				label: labelFor(d.type),
				missing: d.missingParams.join(", ") || "—",
				extra: d.extraParams.join(", ") || "—",
			})),
		);
	}

	if (report.orphan.length > 0) {
		console.log("Orphan products (insurance_type retired from catalog):");
		console.table(
			report.orphan.map((o) => ({
				id: o.id,
				type: o.type,
				label: labelFor(o.type),
			})),
		);
	}

	console.groupEnd();
};
